import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { customersApi } from '../../../api/customers.js';
import { customerDraftsApi } from '../../../api/customerDrafts.js';
import { storesApi } from '../../../api/stores.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

import { CustomerFormModal } from './CustomerFormModal.jsx';
import { CustomerDraftsModal } from './CustomerDraftsModal.jsx';

const STATUS_TONE = { active: 'success', archived: 'neutral' };
const STATUS_LABEL = { active: 'Attivo', archived: 'Archiviato' };

function fullName(c) {
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '-';
}

export function CustomersPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canCreate = hasPermission('customers:create');
  const canUpdate = hasPermission('customers:update');
  const canDelete = hasPermission('customers:delete');

  const storesQuery = useQuery({ queryKey: ['stores', 'filter'], queryFn: () => storesApi.list({ limit: 100 }) });
  const storeMap = useMemo(() => {
    const items = (storesQuery.data && storesQuery.data.items) || [];
    return new Map(items.map((s) => [String(s._id), s]));
  }, [storesQuery.data]);

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['customers', 'list'],
    fetcher: customersApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Personal draft count, to surface the "Bozze" entry point with a badge.
  const draftsQuery = useQuery({
    queryKey: ['customer-drafts', 'list'],
    enabled: canCreate,
    queryFn: () => customerDraftsApi.list({ limit: 50 })
  });
  const draftCount = (draftsQuery.data?.items || []).length;

  const openNewCustomer = () => { setEditing(null); setResumeDraft(null); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditing(null); setResumeDraft(null); };
  const resume = (d) => { setEditing(null); setResumeDraft(d); setDraftsOpen(false); setFormOpen(true); };

  const deleteMutation = useMutation({
    mutationFn: (id) => customersApi.remove(id),
    onSuccess: (res) => {
      toast.success(res && res.deletedPets ? `Cliente eliminato (${res.deletedPets} animali rimossi).` : 'Cliente eliminato.');
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    {
      key: 'name',
      header: 'Cliente',
      render: (r) => (
        <button type="button" className="text-left text-ink hover:underline" onClick={() => navigate(`/admin/customers/${r._id}`)}>
          {fullName(r)}
        </button>
      )
    },
    {
      key: 'contact',
      header: 'Contatti',
      render: (r) => (
        <div className="flex flex-col">
          {r.email && <span className="text-xs text-ink-muted">{r.email}</span>}
          {r.phone && <span className="text-xs text-ink-muted">{r.phone}</span>}
          {!r.email && !r.phone && <span className="text-xs text-ink-faint">-</span>}
        </div>
      )
    },
    {
      key: 'store',
      header: 'Negozio',
      render: (r) => {
        const id = r.storeId && (r.storeId._id || r.storeId);
        const s = storeMap.get(String(id));
        return <span className="text-ink-muted">{(r.storeId && r.storeId.name) || (s && s.name) || '-'}</span>;
      }
    },
    {
      key: 'account',
      header: 'Account',
      render: (r) => r.userId ? <Badge tone="info">collegato</Badge> : <span className="text-xs text-ink-faint">-</span>
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => navigate(`/admin/customers/${r._id}`)}>
            Scheda
          </button>
          {canUpdate && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canDelete && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Clienti"
        description="I proprietari degli animali: scheda cliente, animali e memoria storica."
        actions={canCreate ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setDraftsOpen(true)}>
              Bozze{draftCount > 0 ? ` (${draftCount})` : ''}
            </Button>
            <Button onClick={openNewCustomer}>Nuovo cliente</Button>
          </div>
        ) : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="nome, email, telefono..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select label="Stato" className="w-40" value={filters.status || ''} onChange={(e) => setFilters({ status: e.target.value || undefined })}>
          <option value="">Tutti</option>
          <option value="active">Attivi</option>
          <option value="archived">Archiviati</option>
        </Select>
        <Select label="Negozio" className="w-48" value={filters.storeId || ''} onChange={(e) => setFilters({ storeId: e.target.value || undefined })}>
          <option value="">Tutti</option>
          {Array.from(storeMap.values()).map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun cliente', description: 'Crea la prima scheda cliente.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <CustomerFormModal
        open={formOpen}
        onClose={closeForm}
        initial={editing}
        resumeDraft={resumeDraft}
      />

      <CustomerDraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        onResume={resume}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare il cliente?"
        description={confirmTarget ? `"${fullName(confirmTarget)}" e tutti i suoi animali e foto saranno rimossi. Operazione irreversibile.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
    </div>
  );
}
